import { useState } from 'react'
import { useCreateTemplate, useUpdateTemplate } from '@/hooks/useNotificacoes'
import { X, Loader2, Save, Smartphone, Globe, Mail } from 'lucide-react'
import type { NotificacaoTemplate, NotificacaoCanal, UUID } from '@/types/database'

interface TemplateFormProps {
    template: NotificacaoTemplate | null
    onClose: () => void
}

import { useAuthStore } from '@/stores/auth.store'

export function TemplateForm({ template, onClose }: TemplateFormProps) {
    const user = useAuthStore(s => s.user)
    const createMut = useCreateTemplate()
    const updateMut = useUpdateTemplate()
    const isPending = createMut.isPending || updateMut.isPending

    const [nome, setNome] = useState(template?.nome || '')
    const [descricao, setDescricao] = useState(template?.descricao || '')
    const [canal, setCanal] = useState<NotificacaoCanal>(template?.canal || 'whatsapp')
    const [assunto, setAssunto] = useState(template?.assunto || '')
    const [conteudo, setConteudo] = useState(template?.conteudo || '')
    const [ativo, setAtivo] = useState(template?.ativo ?? true)

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        const payload = {
            nome,
            descricao: descricao || null,
            canal,
            assunto: canal !== 'whatsapp' ? assunto : null,
            conteudo,
            ativo,
            tenant_id: user?.tenant_id
        }

        try {
            if (template) {
                await updateMut.mutateAsync({ id: template.id as UUID, ...payload })
            } else {
                if (!user?.tenant_id) throw new Error('Sessão expirada. Faça login novamente.')
                await createMut.mutateAsync(payload)
            }
            onClose()
        } catch (err) {
            console.error(err)
            alert('Erro ao salvar template')
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4">
            <div className="bg-white w-full max-w-2xl flex flex-col max-h-[90vh] overflow-hidden rounded-3xl shadow-elevated">
                {/* Header */}
                <div className="p-4 border-b border-surface-100 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-surface-900">
                        {template ? `Editar ${template.nome}` : 'Novo Template'}
                    </h3>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-100 text-surface-600 transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Form */}
                <form id="template-form" onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label className="label">Nome do Template</label>
                            <input
                                type="text" required value={nome} onChange={e => setNome(e.target.value)}
                                className="input" placeholder="Ex: Boas-vindas Proteção"
                            />
                        </div>

                        <div>
                            <label className="label">Canal</label>
                            <div className="grid grid-cols-3 gap-2">
                                {[
                                    { id: 'whatsapp', icon: Smartphone, label: 'WhatsApp' },
                                    { id: 'web_push', icon: Globe, label: 'Push' },
                                    { id: 'email', icon: Mail, label: 'Email' }
                                ].map(c => (
                                    <button
                                        key={c.id} type="button"
                                        onClick={() => setCanal(c.id as any)}
                                        className={`flex flex-col items-center gap-1.5 p-2 rounded-xl border transition-all
                                            ${canal === c.id ? 'bg-primary-50 border-primary-200 text-primary-600' : 'bg-white border-surface-100 text-surface-400 hover:bg-surface-50'}`}
                                    >
                                        <c.icon size={18} />
                                        <span className="text-[10px] font-bold uppercase">{c.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex items-end">
                            <label className="flex items-center gap-3 p-3 rounded-xl border border-surface-100 bg-white cursor-pointer flex-1 h-[58px]">
                                <input
                                    type="checkbox" checked={ativo} onChange={e => setAtivo(e.target.checked)}
                                    className="w-4 h-4 rounded text-primary-600 focus:ring-primary-500"
                                />
                                <span className="font-semibold text-surface-900">Ativo</span>
                            </label>
                        </div>

                        {canal !== 'whatsapp' && (
                            <div className="md:col-span-2">
                                <label className="label">Assunto / Título do Push</label>
                                <input
                                    type="text" required value={assunto} onChange={e => setAssunto(e.target.value)}
                                    className="input font-semibold" placeholder="Ex: Aviso Importante!"
                                />
                            </div>
                        )}

                        <div className="md:col-span-2">
                            <label className="label flex justify-between">
                                <span>Conteúdo da Mensagem</span>
                                <span className="text-[10px] text-surface-400 font-bold uppercase">Variáveis: {"{nome}"}, {"{placa}"}, {"{url_manual}"}, {"{url_contrato}"}</span>
                            </label>
                            <textarea
                                required value={conteudo} onChange={e => setConteudo(e.target.value)}
                                className="input min-h-[120px] font-mono text-sm leading-relaxed"
                                placeholder={`Olá {nome},\nSua ativação do serviço {servico} foi concluída com sucesso!`}
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="label">Descrição (Interna)</label>
                            <input
                                type="text" value={descricao} onChange={e => setDescricao(e.target.value)}
                                className="input text-xs" placeholder="Para organização interna..."
                            />
                        </div>
                    </div>
                </form>

                {/* Footer */}
                <div className="p-4 border-t border-surface-100 flex justify-end gap-3">
                    <button type="button" onClick={onClose} className="btn border border-surface-200 bg-white text-surface-700">
                        Cancelar
                    </button>
                    <button type="submit" form="template-form" disabled={isPending}
                        className="btn btn-primary gap-2 shadow-lg shadow-primary-500/20">
                        {isPending ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                        Salvar Template
                    </button>
                </div>
            </div>
        </div>
    )
}
